import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import './AdminEmailAutomation.css';

function AdminEmailAutomation() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [activeTab, setActiveTab] = useState('templates');
  const [campaignsPage, setCampaignsPage] = useState(1);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    template_type: 'abandoned_cart',
    subject: '',
    body_html: '',
    body_text: '',
    is_active: true,
    delay_hours: 0
  });
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ['admin-email-templates'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/email-automation/templates');
      return res.data;
    },
  });

  const { data: campaignsData, isLoading: campaignsLoading } = useQuery({
    queryKey: ['admin-email-campaigns', campaignsPage],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/email-automation/campaigns?page=${campaignsPage}&limit=50`);
      return res.data;
    },
  });

  const createTemplateMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.post('/admin/email-automation/templates', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-email-templates']);
      setShowCreateModal(false);
      setEditingTemplate(null);
      notification.success('Email šablonas sėkmingai sukurtas');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida kuriant email šabloną');
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return apiClient.put(`/admin/email-automation/templates/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-email-templates']);
      setShowCreateModal(false);
      setEditingTemplate(null);
      notification.success('Email šablonas sėkmingai atnaujintas');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida atnaujinant email šabloną');
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.delete(`/admin/email-automation/templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-email-templates']);
      notification.success('Email šablonas pašalintas');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida šalinant email šabloną');
    },
  });

  const testEmailMutation = useMutation({
    mutationFn: async ({ id, email }) => {
      return apiClient.post(`/admin/email-automation/templates/${id}/test`, { email });
    },
    onSuccess: () => {
      notification.success('Test email išsiųstas');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida siunčiant test email');
    },
  });

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setEditForm({
      name: template.name || '',
      template_type: template.template_type || 'abandoned_cart',
      subject: template.subject || '',
      body_html: template.body_html || '',
      body_text: template.body_text || '',
      is_active: template.is_active !== undefined ? template.is_active : true,
      delay_hours: template.delay_hours || 0
    });
    setShowCreateModal(true);
  };

  const handleNew = () => {
    setEditingTemplate(null);
    setEditForm({
      name: '',
      template_type: 'abandoned_cart',
      subject: '',
      body_html: '',
      body_text: '',
      is_active: true,
      delay_hours: 0
    });
    setShowCreateModal(true);
  };

  const handleSave = () => {
    if (!editForm.name || !editForm.subject || !editForm.body_html) {
      notification.warning('Užpildykite privalomus laukus');
      return;
    }

    if (editingTemplate) {
      updateTemplateMutation.mutate({ id: editingTemplate.id, data: editForm });
    } else {
      createTemplateMutation.mutate(editForm);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('lt-LT');
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'pending', label: 'Laukiama' },
      sent: { class: 'sent', label: 'Išsiųsta' },
      failed: { class: 'failed', label: 'Nepavyko' },
      bounced: { class: 'bounced', label: 'Grąžinta' }
    };
    return badges[status] || { class: 'pending', label: status };
  };

  return (
    <div className="admin-email-automation-page container">
      <div className="email-header">
        <h1>El. laiškų automatizacija</h1>
        <button onClick={handleNew} className="btn btn-primary">
          + Pridėti šabloną
        </button>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          Šablonai
        </button>
        <button
          className={`tab ${activeTab === 'campaigns' ? 'active' : ''}`}
          onClick={() => setActiveTab('campaigns')}
        >
          Kampanijos
        </button>
      </div>

      {activeTab === 'templates' && (
        <div className="templates-section">
          {templatesLoading ? (
            <div>Kraunama...</div>
          ) : templates && templates.length > 0 ? (
            <div className="templates-list">
              {templates.map((template) => (
                <div key={template.id} className="template-card">
                  <div className="template-info">
                    <h3>{template.name}</h3>
                    <div className="template-details">
                      <div className="detail-item">
                        <span className="label">Tipas:</span>
                        <span className="value">
                          {template.template_type === 'abandoned_cart' && 'Apleistas krepšelis'}
                          {template.template_type === 'post_purchase' && 'Po pirkimo'}
                          {template.template_type === 'reactivation' && 'Reaktivacija'}
                          {template.template_type === 'newsletter' && 'Naujienlaiškis'}
                          {template.template_type === 'custom' && 'Pasirinktinis'}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Tema:</span>
                        <span className="value">{template.subject}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Vėlavimas:</span>
                        <span className="value">{template.delay_hours} val.</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Statusas:</span>
                        <span className={`status-badge ${template.is_active ? 'active' : 'inactive'}`}>
                          {template.is_active ? 'Aktyvus' : 'Neaktyvus'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="template-actions">
                    <button
                      onClick={() => {
                        const email = prompt('Įveskite el. pašto adresą testui:');
                        if (email) {
                          testEmailMutation.mutate({ id: template.id, email });
                        }
                      }}
                      className="btn btn-sm btn-secondary"
                    >
                      Testas
                    </button>
                    <button
                      onClick={() => handleEdit(template)}
                      className="btn btn-sm btn-primary"
                    >
                      Redaguoti
                    </button>
                    <button
                      onClick={async () => {
                        const confirmed = await notification.showConfirm(
                          `Ar tikrai norite pašalinti šabloną "${template.name}"?`,
                          'Patvirtinti šablono pašalinimą'
                        );
                        if (confirmed) {
                          deleteTemplateMutation.mutate(template.id);
                        }
                      }}
                      className="btn btn-sm btn-danger"
                    >
                      Pašalinti
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>Email šablonų nėra. Sukurkite pirmąjį šabloną.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'campaigns' && (
        <div className="campaigns-section">
          {campaignsLoading ? (
            <div>Kraunama...</div>
          ) : campaignsData?.campaigns && campaignsData.campaigns.length > 0 ? (
            <div className="campaigns-table-wrapper">
              <table className="campaigns-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Šablonas</th>
                    <th>Gavėjas</th>
                    <th>Statusas</th>
                    <th>Išsiųsta</th>
                    <th>Atidaryta</th>
                  </tr>
                </thead>
                <tbody>
                  {campaignsData.campaigns && campaignsData.campaigns.length > 0 ? (
                    campaignsData.campaigns.map((campaign) => {
                    const statusBadge = getStatusBadge(campaign.status);
                    return (
                      <tr key={campaign.id}>
                        <td>{formatDate(campaign.created_at)}</td>
                        <td>{campaign.template_name}</td>
                        <td>{campaign.email}</td>
                        <td>
                          <span className={`status-badge ${statusBadge.class}`}>
                            {statusBadge.label}
                          </span>
                        </td>
                        <td>{formatDate(campaign.sent_at)}</td>
                        <td>{formatDate(campaign.opened_at)}                        </td>
                      </tr>
                    );
                  })) : (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                        Kampanijų nėra
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>Kampanijų nėra</p>
            </div>
          )}

          {/* Puslapiavimas */}
          {campaignsData?.pagination && campaignsData.pagination.pages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCampaignsPage(p => Math.max(1, p - 1))}
                disabled={campaignsPage === 1}
                className="btn btn-secondary"
              >
                Atgal
              </button>
              <span>
                Puslapis {campaignsData.pagination.page} iš {campaignsData.pagination.pages} ({campaignsData.pagination.total} kampanijų)
              </span>
              <button
                onClick={() => setCampaignsPage(p => Math.min(campaignsData.pagination.pages, p + 1))}
                disabled={campaignsPage === campaignsData.pagination.pages}
                className="btn btn-secondary"
              >
                Pirmyn
              </button>
            </div>
          )}
        </div>
      )}

      {/* Redagavimo/Sukūrimo modalas */}
      {showCreateModal && (
        <EmailTemplateModal
          editForm={editForm}
          setEditForm={setEditForm}
          editingTemplate={editingTemplate}
          onSave={handleSave}
          onClose={() => {
            setShowCreateModal(false);
            setEditingTemplate(null);
          }}
          isLoading={createTemplateMutation.isLoading || updateTemplateMutation.isLoading}
        />
      )}
    </div>
  );
}

// Email šablono modalas
function EmailTemplateModal({ editForm, setEditForm, editingTemplate, onSave, onClose, isLoading }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <h2>{editingTemplate ? 'Redaguoti email šabloną' : 'Naujas email šablonas'}</h2>

        <div className="form-group">
          <label>Pavadinimas *</label>
          <input
            type="text"
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            className="input"
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Tipas *</label>
            <select
              value={editForm.template_type}
              onChange={(e) => setEditForm({ ...editForm, template_type: e.target.value })}
              className="input"
            >
              <option value="abandoned_cart">Apleistas krepšelis</option>
              <option value="post_purchase">Po pirkimo</option>
              <option value="reactivation">Reaktivacija</option>
              <option value="newsletter">Naujienlaiškis</option>
              <option value="custom">Pasirinktinis</option>
            </select>
          </div>

          <div className="form-group">
            <label>Vėlavimas (valandos)</label>
            <input
              type="number"
              value={editForm.delay_hours}
              onChange={(e) => setEditForm({ ...editForm, delay_hours: parseInt(e.target.value) || 0 })}
              className="input"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Tema *</label>
          <input
            type="text"
            value={editForm.subject}
            onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
            className="input"
            required
          />
        </div>

        <div className="form-group">
          <label>HTML turinys *</label>
          <textarea
            value={editForm.body_html}
            onChange={(e) => setEditForm({ ...editForm, body_html: e.target.value })}
            className="input"
            rows="10"
            required
          />
          <small>Galite naudoti: {'{cart_items}'}, {'{user_email}'}, {'{order_number}'}, {'{order_total}'}</small>
        </div>

        <div className="form-group">
          <label>Tekstinis turinys</label>
          <textarea
            value={editForm.body_text}
            onChange={(e) => setEditForm({ ...editForm, body_text: e.target.value })}
            className="input"
            rows="6"
          />
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={editForm.is_active}
              onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
            />
            <span>Aktyvus</span>
          </label>
        </div>

        <div className="modal-actions">
          <button onClick={onClose} className="btn btn-secondary">
            Atšaukti
          </button>
          <button
            onClick={onSave}
            disabled={isLoading || !editForm.name || !editForm.subject || !editForm.body_html}
            className="btn btn-primary"
          >
            {isLoading ? 'Išsaugoma...' : 'Išsaugoti'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminEmailAutomation;
